// src/components/manuscriptTheme/ThemeMiniCard.jsx
//
// A thumbnail of a manuscript theme: the opening of a chapter set in it — label,
// title, ornament, the first lines of text. The prototype's `miniCard`, used
// for the preset cards in the Theme Editor and the Theme box in the editor's
// right rail.
//
// Every colour and typeface here is the theme's, which is the author's data —
// not app chrome, so none of it comes from `src/theme.js`. The card's own frame
// does.
import React from 'react';
import { Box } from '@mui/material';
import { tokens } from '../../theme';
import { fontStackFor } from '../../config/fonts';
import { chapterLabel, completeTheme } from '../../utils/manuscriptTheme';

const SAMPLE_TITLE = 'New Grail';
const SAMPLE_TEXT = 'The bell above the door of Gio’s shop had not rung properly since 1987. It gave a kind of cough instead, a dry metallic apology…';

const caseStyle = (textCase) => (
  textCase === 'upper' ? { textTransform: 'uppercase' }
    : textCase === 'smallcaps' ? { textTransform: 'uppercase', fontSize: '0.8em' }
      : { textTransform: 'none' }
);

function Ornament({ kind, color }) {
  if (kind === 'none') return null;
  if (kind === 'rule' || kind === 'flourish') {
    return <span style={{ display: 'inline-block', width: 20, borderTop: `1px solid ${color}`, verticalAlign: 'middle' }} />;
  }
  const glyph = { fleuron: '❦', dots: '• • •', diamond: '◆', stars: '✦' }[kind];
  return <span>{glyph}</span>;
}

export default function ThemeMiniCard({
  theme: themeIn,
  title = SAMPLE_TITLE,
  excerpt = SAMPLE_TEXT,
  height = 98,
  // A preset's label stays switched off until the author turns it on; the card
  // shows the style it would use, so the presets still look like themselves.
  labelStyle,
}) {
  const t = completeTheme(themeIn);
  const { ch: c, col } = t;
  const label = labelStyle || c.label;
  return (
    <Box
      sx={{
        height,
        borderRadius: `${tokens.radius.base}px`,
        p: '12px 10px 0',
        overflow: 'hidden',
        border: `1px solid ${tokens.color.line}`,
        position: 'relative',
        background: col.paper,
        color: col.text,
        textAlign: c.align,
      }}
    >
      {label !== 'none' && (
        <div
          style={{
            fontFamily: fontStackFor(c.lFont),
            fontSize: Math.min(20, Math.max(6.5, c.lSize * 0.6)),
            lineHeight: 1.1,
            letterSpacing: `${c.lTrack}em`,
            color: col.accent,
            ...caseStyle(c.lCase),
          }}
        >
          {chapterLabel(label, 1)}
        </div>
      )}
      <div
        style={{
          fontFamily: fontStackFor(c.tFont),
          fontSize: Math.max(10, Math.min(17, c.tSize * 0.58)),
          fontWeight: c.tWeight,
          fontStyle: c.tItalic ? 'italic' : 'normal',
          lineHeight: 1.15,
          marginTop: 3,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          ...caseStyle(c.tCase),
        }}
      >
        {title}
      </div>
      <div style={{ color: col.accent, fontSize: 8, marginTop: 3, lineHeight: 1 }}>
        <Ornament kind={c.orn} color={col.accent} />
      </div>
      <div
        style={{
          marginTop: 7,
          fontSize: 7,
          lineHeight: 1.5,
          opacity: 0.85,
          textAlign: t.body.align,
          fontFamily: fontStackFor(t.body.font),
        }}
      >
        {excerpt}
      </div>
    </Box>
  );
}
